import { useCallback, useState } from 'react';
import { logger } from '@fiverr-private/obs';
import { raiseToast } from '@fiverr-private/toast';
import { globalMixpanelReporter, globalBigQueryReporter, globalGrafanaReporter } from '../../../events';
import { resendEmailWithCode } from '../../../api';
import { isAxiosError } from '../../../utils/isAxiosError';
import { ResendEmailWithCodeResponse } from '../../../api/resendEmailWithCode';
import { appTranslate } from '../../../i18n';

interface UseResendEmailWithCode {
    email: string;
    setShowResendSuccess?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useResendEmailWithCode = ({ email, setShowResendSuccess }: UseResendEmailWithCode) => {
    const [isResending, setIsResending] = useState(false);
    const resend = useCallback(async () => {
        setIsResending(true);
        try {
            globalMixpanelReporter.send('Identification - Activation - Resend Code');
            globalBigQueryReporter.send('identification.activation.resend_code');
            globalGrafanaReporter.count('identification.%{platform}.activation.resend_code');

            const result = await resendEmailWithCode(email);

            if (result.translatedError) {
                raiseToast({ message: result.translatedError, type: 'error', location: 'top-end' });
            }
        } catch (error) {
            if (isAxiosError(error)) {
                const errorMessage = (error.response?.data as ResendEmailWithCodeResponse | undefined)?.error;
                logger.error({
                    message: `Resend Activation Email Error: ${errorMessage}`,
                    package: '@fiverr-private/code_activation',
                });
                raiseToast({
                    message: errorMessage || appTranslate<string>('errors.general.unspecified'),
                    type: 'error',
                    location: 'top-end',
                });
            } else if (error instanceof Error) {
                logger.error({
                    message: `Resend Activation Email Error: ${error.message}`,
                    package: '@fiverr-private/code_activation',
                });
                raiseToast({
                    message: appTranslate<string>('errors.general.unspecified'),
                    type: 'error',
                    location: 'top-end',
                });
            }
        } finally {
            setShowResendSuccess?.(false);
        }
        setIsResending(false);
    }, [email, setShowResendSuccess]);

    return {
        isResending,
        resend,
    };
};
